'use client';

import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { type ArtistProgramApplyStatus, type ArtistProgramMy, getMy } from '../_apis';

type LoadState = 'idle' | 'loading' | 'success' | 'error';
type StatusContext = {
  error?: unknown;
  loadState: LoadState;
  my?: ArtistProgramMy;
  status?: ArtistProgramApplyStatus;
  refresh: (options?: { force?: boolean }) => Promise<ArtistProgramMy>;
  setMy: (my: ArtistProgramMy) => void;
};

const Status = createContext<StatusContext | undefined>(undefined);

export function ArtistProgramStatusProvider({ children }: { children: ReactNode }) {
  const [my, setMyState] = useState<ArtistProgramMy>();
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [error, setError] = useState<unknown>();
  const myRef = useRef<ArtistProgramMy | undefined>(undefined);
  const requestRef = useRef<Promise<ArtistProgramMy> | null>(null);

  const updateMy = useCallback((nextMy: ArtistProgramMy) => {
    myRef.current = nextMy;
    setMyState(nextMy);
  }, []);

  const refresh = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!force && myRef.current) return myRef.current;
      if (!force && requestRef.current) return requestRef.current;
      const request = getMy();
      requestRef.current = request;
      setLoadState('loading');
      setError(undefined);
      try {
        const nextMy = await request;
        updateMy(nextMy);
        setLoadState('success');
        return nextMy;
      } catch (nextError) {
        setError(nextError);
        setLoadState('error');
        throw nextError;
      } finally {
        if (requestRef.current === request) requestRef.current = null;
      }
    },
    [updateMy],
  );

  const value = useMemo(
    () => ({ error, loadState, my, refresh, setMy: updateMy, status: my?.status }),
    [error, loadState, my, refresh, updateMy],
  );
  return <Status.Provider value={value}>{children}</Status.Provider>;
}

export function useArtistProgramStatus() {
  const value = useContext(Status);
  if (!value) throw new Error('useArtistProgramStatus must be used within ArtistProgramStatusProvider');
  return value;
}
